import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useCartStore } from '../store/useCartStore';
import { ordersService } from '../services/orders';
import { productsService } from '../services/products';
import { BillingAddress, ShippingAddress } from '../types/woocommerce';
import { 
  ShoppingBagIcon, 
  CreditCardIcon, 
  TruckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

interface CheckoutForm {
  billing: BillingAddress;
  shipping: ShippingAddress;
  sameAsBilling: boolean;
  payment_method: string;
  customer_note: string;
  acceptTerms: boolean;
}

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, getSubtotal, clearCart } = useCartStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<CheckoutForm>({
    defaultValues: {
      billing: {
        country: 'FR',
      },
      shipping: {
        country: 'FR',
      },
      sameAsBilling: true,
      payment_method: 'bacs',
      acceptTerms: false,
    },
  });

  const sameAsBilling = watch('sameAsBilling');
  const subtotal = getSubtotal();
  const shipping = ordersService.calculateShipping(subtotal);
  const total = subtotal + shipping;

  // Mutation pour créer la commande
  const createOrderMutation = useMutation({
    mutationFn: (data: CheckoutForm) => {
      const shippingAddress = data.sameAsBilling ? data.billing : data.shipping;
      return ordersService.createOrderFromCart(
        data.billing,
        shippingAddress as ShippingAddress,
        data.payment_method,
        data.customer_note
      );
    },
    onSuccess: (order) => {
      setOrderSuccess(order.id);
      clearCart();
      // Rediriger vers la page de confirmation après 2 secondes
      setTimeout(() => {
        navigate(`/order-confirmation/${order.id}`);
      }, 2000);
    },
    onError: (error: any) => {
      setIsProcessing(false);
      console.error('Erreur lors de la création de la commande:', error);
      setError('root', {
        message: error.response?.data?.message || 'Une erreur est survenue lors de la création de la commande',
      });
    },
  });

  const onSubmit = async (data: CheckoutForm) => {
    if (!data.acceptTerms) {
      setError('acceptTerms', {
        message: 'Vous devez accepter les conditions générales de vente',
      });
      return;
    }

    // Valider l'adresse de facturation
    const billingErrors = ordersService.validateBillingAddress(data.billing);
    if (billingErrors.length > 0) {
      billingErrors.forEach((error, index) => {
        setError('root', { message: error });
      });
      return;
    }

    setIsProcessing(true);
    createOrderMutation.mutate(data);
  };

  if (items.length === 0 && !orderSuccess) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <ShoppingBagIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            Votre panier est vide
          </h2>
          <p className="text-gray-500 mb-6">
            Ajoutez des produits à votre panier avant de passer commande
          </p>
          <button
            onClick={() => navigate('/products')}
            className="btn-primary"
          >
            Voir nos produits
          </button>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-md mx-auto text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Commande confirmée !
          </h2>
          <p className="text-gray-600 mb-6">
            Votre commande a été créée avec succès. Vous allez être redirigé vers la page de confirmation...
          </p>
          <div className="inline-flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-custom py-8">
        <h1 className="text-3xl font-bold mb-8">Finaliser la commande</h1>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Formulaire principal */}
            <div className="lg:col-span-2 space-y-6">
              {/* Adresse de facturation */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CreditCardIcon className="h-6 w-6 text-gray-400" />
                  Adresse de facturation
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      {...register('billing.first_name', { required: 'Le prénom est requis' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.first_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.first_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      {...register('billing.last_name', { required: 'Le nom est requis' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.last_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.last_name.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      {...register('billing.email', { 
                        required: 'L\'email est requis',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Email invalide'
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.email.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      {...register('billing.phone', { required: 'Le téléphone est requis' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.phone.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse *
                    </label>
                    <input
                      type="text"
                      {...register('billing.address_1', { required: 'L\'adresse est requise' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.address_1 && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.address_1.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complément d'adresse
                    </label>
                    <input
                      type="text"
                      {...register('billing.address_2')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville *
                    </label>
                    <input
                      type="text"
                      {...register('billing.city', { required: 'La ville est requise' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.city.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      {...register('billing.postcode', { required: 'Le code postal est requis' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.billing?.postcode && (
                      <p className="text-red-500 text-sm mt-1">{errors.billing.postcode.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Adresse de livraison */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <TruckIcon className="h-6 w-6 text-gray-400" />
                  Adresse de livraison
                </h2>

                <label className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    {...register('sameAsBilling')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Identique à l'adresse de facturation
                  </span>
                </label>

                {!sameAsBilling && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        {...register('shipping.first_name', { 
                          required: !sameAsBilling ? 'Le prénom est requis' : false 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        {...register('shipping.last_name', { 
                          required: !sameAsBilling ? 'Le nom est requis' : false 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adresse *
                      </label>
                      <input
                        type="text"
                        {...register('shipping.address_1', { 
                          required: !sameAsBilling ? 'L\'adresse est requise' : false 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Complément d'adresse
                      </label>
                      <input
                        type="text"
                        {...register('shipping.address_2')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ville *
                      </label>
                      <input
                        type="text"
                        {...register('shipping.city', { 
                          required: !sameAsBilling ? 'La ville est requise' : false 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal *
                      </label>
                      <input
                        type="text"
                        {...register('shipping.postcode', { 
                          required: !sameAsBilling ? 'Le code postal est requis' : false 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Mode de paiement */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">Mode de paiement</h2>
                
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="bacs"
                      {...register('payment_method')}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="font-medium">Virement bancaire</p>
                      <p className="text-sm text-gray-600">
                        Effectuez un virement directement sur notre compte bancaire
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="cheque"
                      {...register('payment_method')}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="font-medium">Paiement par chèque</p>
                      <p className="text-sm text-gray-600">
                        Envoyez-nous votre chèque par courrier
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="cod"
                      {...register('payment_method')}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="font-medium">Paiement à la livraison</p>
                      <p className="text-sm text-gray-600">
                        Payez en espèces à la réception de votre commande
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Note de commande */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">Note de commande (optionnel)</h2>
                <textarea
                  {...register('customer_note')}
                  rows={4}
                  placeholder="Ajoutez une note concernant votre commande..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Résumé de la commande */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
                <h2 className="text-xl font-semibold mb-4">Résumé de la commande</h2>
                
                <div className="space-y-3 mb-4">
                  {items.map((item) => {
                    const price = item.variation
                      ? parseFloat(item.variation.price)
                      : parseFloat(item.product.price);

                    return (
                      <div key={`${item.product.id}-${item.variationId || 0}`} className="flex items-center gap-3">
                        <div className="w-16 h-16 flex-shrink-0">
                          {item.product.images.length > 0 ? (
                            <img
                              src={item.product.images[0].src}
                              alt={item.product.name}
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                              <span className="text-gray-400 text-xs">Pas d'image</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium line-clamp-1">{item.product.name}</p>
                          {item.variation && (
                            <p className="text-xs text-gray-500">
                              {item.variation.attributes.map(a => a.option).join(', ')}
                            </p>
                          )}
                          <p className="text-sm text-gray-600">Qté: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-medium">
                          {productsService.getFormattedPrice(price * item.quantity)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Sous-total</span>
                    <span>{productsService.getFormattedPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Livraison</span>
                    <span>
                      {shipping === 0 ? 'Gratuite' : productsService.getFormattedPrice(shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span>{productsService.getFormattedPrice(total)}</span>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="flex items-start gap-2 mb-4">
                    <input
                      type="checkbox"
                      {...register('acceptTerms', { 
                        required: 'Vous devez accepter les conditions générales' 
                      })}
                      className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      J'accepte les <a href="/cgv" className="text-primary-600 hover:underline">conditions générales de vente</a>
                    </span>
                  </label>
                  {errors.acceptTerms && (
                    <p className="text-red-500 text-sm mb-2">{errors.acceptTerms.message}</p>
                  )}

                  {errors.root && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800 flex items-center gap-2">
                        <ExclamationCircleIcon className="h-5 w-5" />
                        {errors.root.message}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Traitement en cours...
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="h-5 w-5" />
                        Valider la commande
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

